"use client";

import {useId, useLayoutEffect, useMemo, useRef, useState} from "react";
import {getSlashCommandQuery} from "@/utils/chat/slashCommand";
import type {Skill} from "../SkillCard";
import type {ChatInputProps} from "./types";

type SlashCommandMenuParams = Pick<ChatInputProps, "value" | "onChange" | "skills" | "isLoadingSkills" | "disabled"> & {
   inputRef: React.RefObject<HTMLTextAreaElement | null>;
};

export function useSlashCommandMenu({
   value,
   onChange,
   skills,
   isLoadingSkills = false,
   disabled = false,
   inputRef,
}: SlashCommandMenuParams) {
   const listboxId = useId();
   const [selection, setSelection] = useState<{start: number; end: number} | null>(null);
   const [dismissedValue, setDismissedValue] = useState<string | null>(null);
   const [activeIndex, setActiveIndex] = useState(0);
   const pendingCaret = useRef<{value: string; position: number} | null>(null);
   const query = selection ? getSlashCommandQuery(value, selection.start, selection.end) : null;
   const isOpen = !disabled && (skills !== undefined || isLoadingSkills) && !!query && dismissedValue !== value;
   const search = query?.query.toLocaleLowerCase() ?? "";
   const matches = useMemo(
      () => isLoadingSkills ? [] : (skills ?? []).filter((skill) =>
         `${skill.slug} ${skill.title} ${skill.description}`.toLocaleLowerCase().includes(search)
      ),
      [skills, isLoadingSkills, search]
   );
   const selectedIndex = Math.min(activeIndex, Math.max(0, matches.length - 1));
   const getOptionId = (index: number) => `${listboxId}-option-${index}`;

   // Restore the caret after React commits the controlled value. The ref is
   // updated on interaction so selection returns to the visible mobile input.
   useLayoutEffect(() => {
      const pending = pendingCaret.current;
      if (!pending || pending.value !== value) return;
      pendingCaret.current = null;
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(pending.position, pending.position);
      setSelection({start: pending.position, end: pending.position});
   });

   const updateSelection = (element: HTMLTextAreaElement) => {
      inputRef.current = element;
      setSelection({start: element.selectionStart, end: element.selectionEnd});
   };

   const dismiss = () => setDismissedValue(value);

   const selectSkill = (skill: Skill) => {
      if (!query || disabled) return;
      const suffix = value.slice(query.end);
      const command = `/${skill.slug}`;
      const separator = /^\s/.test(suffix) ? "" : " ";
      const nextValue = value.slice(0, query.start) + command + separator + suffix;
      const position = query.start + command.length + 1;
      pendingCaret.current = {value: nextValue, position};
      setDismissedValue(nextValue);
      onChange(nextValue);
   };

   const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (!isOpen || event.nativeEvent.isComposing || event.keyCode === 229) return false;
      if (event.metaKey || event.ctrlKey || event.altKey) return false;

      if (event.key === "Escape") {
         event.preventDefault();
         event.stopPropagation();
         dismiss();
         return true;
      }
      if ((event.key === "ArrowDown" || event.key === "ArrowUp") && !event.shiftKey) {
         event.preventDefault();
         const direction = event.key === "ArrowDown" ? 1 : -1;
         setActiveIndex(matches.length ? (selectedIndex + direction + matches.length) % matches.length : 0);
         return true;
      }
      if ((event.key === "Enter" || event.key === "Tab") && !event.shiftKey) {
         const skill = matches[selectedIndex];
         if (skill) {
            event.preventDefault();
            selectSkill(skill);
            return true;
         }
         // Loading or an empty search should never send a partial command.
         if (event.key === "Enter") {
            event.preventDefault();
            return true;
         }
      }
      if (event.key === "Tab") dismiss();
      return false;
   };

   return {
      isOpen,
      listboxId,
      matches,
      selectedIndex,
      setActiveIndex,
      getOptionId,
      activeOptionId: isOpen && matches.length ? getOptionId(selectedIndex) : undefined,
      dismiss,
      selectSkill,
      handleKeyDown,
      handleChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => {
         updateSelection(event.currentTarget);
         setActiveIndex(0);
         setDismissedValue(null);
         onChange(event.currentTarget.value);
      },
      handleFocus: (event: React.FocusEvent<HTMLTextAreaElement>) => {
         updateSelection(event.currentTarget);
         setActiveIndex(0);
         setDismissedValue(null);
      },
      handleSelect: (event: React.SyntheticEvent<HTMLTextAreaElement>) => updateSelection(event.currentTarget),
      handleBlur: () => setSelection(null),
   };
}
